import { useConversationMessageMediaContentState } from '@modules/conversation/ConversationMediaContentView/useConversationMessageMediaContent';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { GatheringRequests } from 'src/requests/gathering';
import { QueryKey } from 'src/utils/react-query';
import { ConversationMessageContentType } from '../../../../../@types/conversation';
import { IGatheringChatItem } from '../types';
import { getSameDateAndAuthor } from './utils';

const MAX_MESSAGES_PAGE = 50;

export const useGatheringChatMessagesQuery = (sessionRef: string) => {
  const { checkInitialMediaContentDownload } =
    useConversationMessageMediaContentState();

  const {
    data,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
    refetch,
    dataUpdatedAt,
  } = useInfiniteQuery({
    enabled: !!sessionRef,
    queryKey: [QueryKey.GATHERING, sessionRef, 'chatMessages'],
    queryFn: ({ pageParam = null }) =>
      GatheringRequests.fetchGatheringChatMessages(sessionRef, {
        before: pageParam,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.length >= MAX_MESSAGES_PAGE
        ? { before: lastPage?.at(-1)?._id }
        : false,
    getPreviousPageParam: (firstPage) =>
      firstPage.length >= MAX_MESSAGES_PAGE
        ? { after: firstPage?.[0]?._id }
        : false,
    refetchOnWindowFocus: false,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const messages = useMemo(() => {
    if (!data) return [];
    const items: IGatheringChatItem[] = [];

    for (let i = data.pages.length - 1; i >= 0; i -= 1) {
      const currentPage = data?.pages[i];
      for (let j = currentPage.length - 1; j >= 0; j -= 1) {
        const message = currentPage[j];
        const previousMessage = currentPage[j + 1];

        const updatedMessage = getSameDateAndAuthor(message, previousMessage);

        items.push(updatedMessage);

        if (
          updatedMessage.contentType === ConversationMessageContentType.VIDEO &&
          !!updatedMessage.mediaThumbnailRef
        ) {
          checkInitialMediaContentDownload(updatedMessage, 'mediaThumbnailRef');
        }

        checkInitialMediaContentDownload(updatedMessage);
      }
    }

    return items;
  }, [data, dataUpdatedAt]);

  return {
    messages,
    hasNextPage,
    hasPreviousPage,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
    refetch,
    fetchNextPage,
    fetchPreviousPage,
  };
};
